<template>
  <div class="drawer">
    <input
      id="admin-drawer"
      ref="drawerToggle"
      type="checkbox"
      class="drawer-toggle"
    >
    <div class="drawer-content flex flex-col">
      <!-- Navbar -->
      <div
        ref="navbar"
        class="navbar bg-base-200"
      >
        <div class="flex-none lg:hidden">
          <label
            for="admin-drawer"
            class="btn btn-square btn-ghost"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              class="inline-block w-6 h-6 stroke-current"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </label>
        </div>
        <div class="flex-1 px-2 mx-2">
          <router-link to="/admin/trading-dashboard">
            <BrandComponent />
          </router-link>
        </div>
        <div class="flex-none hidden lg:block">
          <ul class="menu menu-horizontal">
            <li v-if="canAccessAdminPage('trading-dashboard')">
              <router-link to="/admin/trading-dashboard">
                Dashboard
              </router-link>
            </li>
            <li v-if="canAccessAdminPage('bots') || isStaff">
              <router-link to="/admin/bots">
                Bots
              </router-link>
            </li>
            <li v-if="canAccessAdminPage('positions')">
              <router-link to="/admin/positions">
                Positions
              </router-link>
            </li>
            <li v-if="canAccessAdminPage('cointegration') || canAccessAdminPage('baskets')">
              <router-link to="/admin/signals">
                Signals
              </router-link>
            </li>
            <li v-if="canAccessAdminPage('backtests')">
              <router-link to="/admin/backtests">
                Backtests
              </router-link>
            </li>
            <li v-if="canAccessAdminPage('backtests') || isStaff">
              <router-link to="/admin/agents">
                Agent
              </router-link>
            </li>
            <li v-if="canAccessAdminPage('loans')">
              <router-link to="/admin/ins-loans">
                Loans
              </router-link>
            </li>
            <li v-if="canAccessAdminPage('accounts') || isStaff">
              <router-link to="/admin/accounts">
                Accounts
              </router-link>
            </li>
            <li v-if="canAccessAdminPage('scaled-orders')">
              <router-link to="/admin/tools">
                Tools
              </router-link>
            </li>
            <li v-if="canAccessAdminPage('performance')">
              <router-link to="/admin/performance">
                Performance
              </router-link>
            </li>
            <li v-if="canAccessAdminPage('profit-sharing')">
              <router-link to="/admin/profit-sharing">
                Profit Sharing
              </router-link>
            </li>
            <li v-if="canAccessAdminPage('strategies') || canAccessAdminPage('global-settings') || isStaff">
              <router-link to="/admin/settings/strategies">
                Settings
              </router-link>
            </li>
            <li v-if="canAccessAdminPage('brand-members')">
              <router-link to="/admin/brand-members">
                Members
              </router-link>
            </li>
            <li>
              <router-link to="/dashboard">
                Client Site
              </router-link>
            </li>
            <li><a @click="handleLogout">Logout</a></li>
          </ul>
        </div>
      </div>

      <!-- Animated FAB for mobile -->
      <transition name="fade-slide">
        <label
          v-if="showFAB"
          for="admin-drawer"
          class="btn btn-primary btn-circle fixed right-4 bottom-4 lg:hidden z-50 opacity-90 hover:opacity-100 shadow-lg transition-opacity duration-200"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            class="inline-block w-6 h-6 stroke-current"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </label>
      </transition>
    </div>
    <!-- Sidebar / Drawer -->
    <div class="drawer-side">
      <label
        for="admin-drawer"
        class="drawer-overlay"
      />
      <div class="menu p-4 w-[300px] h-full bg-base-300 text-base-content">
        <div class="mb-4">
          <BrandComponent />
        </div>
        <ul class="space-y-2">
          <li v-if="canAccessAdminPage('trading-dashboard')">
            <router-link
              to="/admin/trading-dashboard"
              class="flex items-center p-2 rounded-lg hover:bg-base-200"
              @click="closeDrawer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
              </svg>
              Dashboard
            </router-link>
          </li>
          <li v-if="canAccessAdminPage('bots') || isStaff">
            <router-link
              to="/admin/bots"
              class="flex items-center p-2 rounded-lg hover:bg-base-200"
              @click="closeDrawer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                  clip-rule="evenodd"
                />
              </svg>
              Bots
            </router-link>
          </li>
          <li v-if="canAccessAdminPage('positions')">
            <router-link
              to="/admin/positions"
              class="flex items-center p-2 rounded-lg hover:bg-base-200"
              @click="closeDrawer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 0l-2 2a1 1 0 101.414 1.414L8 10.414l1.293 1.293a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
              Positions
            </router-link>
          </li>
          <li v-if="canAccessAdminPage('cointegration') || canAccessAdminPage('baskets')">
            <router-link
              to="/admin/signals"
              class="flex items-center p-2 rounded-lg hover:bg-base-200"
              @click="closeDrawer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11 4a1 1 0 10-2 0v4a1 1 0 102 0V7zm-3 1a1 1 0 10-2 0v3a1 1 0 102 0V8zM8 9a1 1 0 00-2 0v2a1 1 0 102 0V9z"
                  clip-rule="evenodd"
                />
              </svg>
              Signals
            </router-link>
          </li>
          <li v-if="canAccessAdminPage('backtests')">
            <router-link
              to="/admin/backtests"
              class="flex items-center p-2 rounded-lg hover:bg-base-200"
              @click="closeDrawer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"
                />
              </svg>
              Backtests
            </router-link>
          </li>
          <li v-if="canAccessAdminPage('backtests') || isStaff">
            <router-link
              to="/admin/agents"
              class="flex items-center p-2 rounded-lg hover:bg-base-200"
              @click="closeDrawer"
            >
              Agent Workspace
            </router-link>
          </li>
          <li v-if="canAccessAdminPage('loans')">
            <router-link
              to="/admin/ins-loans"
              class="flex items-center p-2 rounded-lg hover:bg-base-200"
              @click="closeDrawer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                  clip-rule="evenodd"
                />
              </svg>
              Loans
            </router-link>
          </li>
          <li v-if="canAccessAdminPage('accounts') || isStaff">
            <router-link
              to="/admin/accounts"
              class="flex items-center p-2 rounded-lg hover:bg-base-200"
              @click="closeDrawer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"
                />
              </svg>
              Accounts
            </router-link>
          </li>
          <li v-if="canAccessAdminPage('scaled-orders')">
            <router-link
              to="/admin/tools"
              class="flex items-center p-2 rounded-lg hover:bg-base-200"
              @click="closeDrawer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M6.672 1.911a1 1 0 10-1.932.518l.259.966a1 1 0 001.932-.518l-.26-.966zM2.429 4.74a1 1 0 10-.517 1.932l.966.259a1 1 0 00.517-1.932l-.966-.26zm8.814-.569a1 1 0 00-1.415-1.414l-.707.707a1 1 0 101.415 1.415l.707-.708zm-7.071 7.072l.707-.707A1 1 0 003.465 9.12l-.708.707a1 1 0 001.415 1.415zm3.2-5.171a1 1 0 00-1.3 1.3l4 10a1 1 0 001.823.075l1.38-2.759 3.018 3.02a1 1 0 001.414-1.415l-3.019-3.02 2.76-1.379a1 1 0 00-.076-1.822l-10-4z"
                  clip-rule="evenodd"
                />
              </svg>
              Tools
            </router-link>
          </li>
          <li v-if="canAccessAdminPage('performance')">
            <router-link
              to="/admin/performance"
              class="flex items-center p-2 rounded-lg hover:bg-base-200"
              @click="closeDrawer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" />
                <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z" />
              </svg>
              Performance
            </router-link>
          </li>
          <li v-if="canAccessAdminPage('profit-sharing')">
            <router-link
              to="/admin/profit-sharing"
              class="flex items-center p-2 rounded-lg hover:bg-base-200"
              @click="closeDrawer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"
                />
              </svg>
              Profit Sharing
            </router-link>
          </li>
          <li v-if="canAccessAdminPage('strategies') || canAccessAdminPage('global-settings') || isStaff">
            <router-link
              to="/admin/settings/strategies"
              class="flex items-center p-2 rounded-lg hover:bg-base-200"
              @click="closeDrawer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                  clip-rule="evenodd"
                />
              </svg>
              Settings
            </router-link>
          </li>
          <li v-if="canAccessAdminPage('brand-members')">
            <router-link
              to="/admin/brand-members"
              class="flex items-center p-2 rounded-lg hover:bg-base-200"
              @click="closeDrawer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"
                />
              </svg>
              Members
            </router-link>
          </li>
          <li>
            <router-link
              to="/dashboard"
              class="flex items-center p-2 rounded-lg hover:bg-base-200"
              @click="closeDrawer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z"
                  clip-rule="evenodd"
                />
              </svg>
              Client Site
            </router-link>
          </li>
        </ul>
        <div class="mt-auto pt-4 border-t border-base-200">
          <a
            class="flex items-center p-2 rounded-lg hover:bg-base-200 text-error"
            @click="handleLogout"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z"
                clip-rule="evenodd"
              />
            </svg>
            Logout
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BrandComponent from "@/components/BrandComponent.vue";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "AdminNavbarComponent",
  components: {
    BrandComponent,
  },
  data() {
    return {
      showFAB: false,
      lastScrollPosition: 0,
      navbarHeight: 0,
    };
  },
  computed: {
    ...mapGetters("auth", ["isAdmin", "isStaff", "isSuperAdmin", "hasGrantedAccess", "canAccessAdminPage"]),
  },
  mounted() {
    this.navbarHeight = this.$refs.navbar?.offsetHeight || 0;
    window.addEventListener("scroll", this.onScroll);
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.onScroll);
  },
  methods: {
    ...mapActions("auth", ["logout"]),
    async handleLogout() {
      try {
        await this.logout();
        this.closeDrawer();
        // The router navigation is now handled in the store action
      } catch (error) {
        console.error("Logout failed:", error);
      }
    },
    onScroll() {
      const currentScrollPosition =
        window.pageYOffset || document.documentElement.scrollTop;
      if (currentScrollPosition < 0) {
        return;
      }
      // Show FAB when scrolled past navbar
      this.showFAB = currentScrollPosition > this.navbarHeight;
      this.lastScrollPosition = currentScrollPosition;
    },
    closeDrawer() {
      if (this.$refs.drawerToggle) {
        this.$refs.drawerToggle.checked = false;
      }
    },
  },
};
</script>

<style scoped>
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Updated styles */
.drawer-side {
  z-index: 999;
}

.drawer-overlay {
  z-index: 998;
}

.menu {
  z-index: 1000;
}

/* Add smooth transition for hover effects */
.menu a {
  transition: background-color 0.2s ease;
}
</style>
